// MUI
import {
  Grid,
  Typography,
  Divider,
  Box,
  Tooltip,
  ListItemProps,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// MUI icons
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";

// Project import
import IconButton from "components/@extended/IconButton";
import { useTranslation } from "utils/locales/utilityFunctions";
import NatureSection from "./natureSection";
import SpecialConditionsSection from "./specialConditionsSection";
import HarvestSection from "./harvestSection";
import YesNoCheckBoxes from "components/input/yesNoCheckboxes";
import { ActivityBiomassScreening } from "types/firestoreFeatures";

type ForestBiomassFormProps = {
  biomassScreeningData: ActivityBiomassScreening;
  inputDisabled: boolean;
  derivedActivityData?: {
    totalArea: number;
    largestArea: number;
  };
  files: ListItemProps[][];
  setBiomassScreeningData: ({
    value,
    key,
    parent,
  }: {
    value: any;
    key: string;
    parent?: string;
  }) => void;
  setOpenFilePickerModal: (state: boolean, categories?: string[]) => void;
};

const ForestBiomassForm = ({
  biomassScreeningData,
  inputDisabled,
  derivedActivityData,
  files,
  setBiomassScreeningData,
  setOpenFilePickerModal,
}: ForestBiomassFormProps) => {
  const { translate } = useTranslation();
  const loading = false;
  const error = false;

  const renderUploadButton = (mode: "dark" | "light", categories: string[], disabled?: boolean) => {
    const disabledButton = loading || inputDisabled || disabled;
    return (
      <Tooltip title={translate("biomass-project-form-upload-doc-tooltip")}>
        <span>
          <IconButton
            variant={error ? "outlined" : "light"}
            color={error ? "error" : "secondary"}
            sx={{ bgcolor: mode === "dark" ? "secondary.light" : null }}
            onClick={() => setOpenFilePickerModal(true, ["screening", ...categories])}
            disabled={disabledButton}
          >
            <FileUploadRoundedIcon fontSize="small" color={loading || disabledButton ? "disabled" : "primary"} />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <>
      {/* ------------------- Status ------------------- */}
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ mb: -1 }}>
          {translate("biomass-project-form-status")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <YesNoCheckBoxes
          title={translate("biomass-project-form-protected-forest")}
          yesLabel={translate("biomass-project-form-yes")}
          noLabel={translate("biomass-project-form-no")}
          state={biomassScreeningData?.status?.protectedForest}
          setState={(val) =>
            setBiomassScreeningData({ value: val, key: "protectedForest", parent: "status" })
          }
          disabled={inputDisabled}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <YesNoCheckBoxes
          title={translate("biomass-project-form-green-plan")}
          yesLabel={translate("biomass-project-form-yes")}
          noLabel={translate("biomass-project-form-no")}
          state={biomassScreeningData?.status?.greenOperationsPlan}
          setState={(val) =>
            setBiomassScreeningData({ value: val, key: "greenOperationsPlan", parent: "status" })
          }
          disabled={inputDisabled}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <Typography color="secondary">
          {translate("biomass-project-form-protection-map")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={biomassScreeningData?.status?.constraintsMap === "yes" ? true : false}
              onChange={(_, checked) =>
                setBiomassScreeningData({
                  value: checked ? "yes" : "no",
                  key: "constraintsMap",
                  parent: "status",
                })
              }
              icon={
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "white",
                    border: inputDisabled ? "1px solid #bfbfbf" : "1px solid #7a7a7a",
                  }}
                />
              }
              disabled={inputDisabled}
            />
          }
          label={translate("biomass-project-form-protection-map-delivered")}
          sx={{ ml: 0, mb: -1 }}
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 1 }}>
        <Divider />
      </Grid>

      {/* ------------------- Species and harvest ------------------- */}
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ mb: -1 }}>
          {translate("biomass-project-form-vegetation-and-intervention")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <YesNoCheckBoxes
          title={translate("biomass-project-form-undergrowth-old-forest")}
          yesLabel={translate("biomass-project-form-yes")}
          noLabel={translate("biomass-project-form-no")}
          state={biomassScreeningData?.standAndActivity?.undergrowthInOldForest}
          tooltipText={translate("tooltip-biomass-project-form-undergrowthInOldForest")}
          setState={(val) =>
            setBiomassScreeningData({
              value: val,
              key: "undergrowthInOldForest",
              parent: "standAndActivity",
            })
          }
          disabled={inputDisabled}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <YesNoCheckBoxes
          title={translate("biomass-project-form-uneven-broadleaves")}
          yesLabel={translate("biomass-project-form-yes")}
          noLabel={translate("biomass-project-form-no")}
          state={biomassScreeningData?.standAndActivity?.unevenAgedFoliage}
          setState={(val) =>
            setBiomassScreeningData({
              value: val,
              key: "unevenAgedFoliage",
              parent: "standAndActivity",
            })
          }
          disabled={inputDisabled}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ bgcolor: "secondary.lighter", p: 2, borderRadius: 1, mt: 1 }}>
          <HarvestSection
            biomassScreeningData={biomassScreeningData}
            derivedActivityData={derivedActivityData}
            setBiomassScreeningData={setBiomassScreeningData}
            inputDisabled={inputDisabled}
            renderUploadButton={renderUploadButton}
            onUploadRegenerationAgreement={setOpenFilePickerModal}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ mt: 1 }} />
      </Grid>

      {/* ------------------- Nature ------------------- */}
      <NatureSection
        biomassScreeningData={biomassScreeningData}
        inputDisabled={inputDisabled}
        setBiomassScreeningData={setBiomassScreeningData}
        renderUploadButton={renderUploadButton}
      />

      <Grid item xs={12}>
        <Divider sx={{ mt: 1 }} />
      </Grid>

      {/* ------------------- Special req. ------------------- */}
      <SpecialConditionsSection
        customerReq={biomassScreeningData?.customerRequirement || ""}
        inputDisabled={inputDisabled}
        setCustomerReq={(val) =>
          setBiomassScreeningData({ value: val, key: "customerRequirement" })
        }
      />

      {/* ------------------- Files ------------------- */}
      {/* {files.length > 0 && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>
          <FilesSection files={files} />
        </>
      )} */}
    </>
  );
};

export default ForestBiomassForm;
